import { css } from '@emotion/css'
import { Node, createNodeFromContent, mergeAttributes } from '@tiptap/core'
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state'
import { ReactNodeViewRenderer } from '@tiptap/react'
import classNames from 'classnames'
import { TextEditorAsset } from '../../types'
import { createInsertHandler } from './handleInsertEvent'
import AssetNodeView from './NodeView'
import { resizableImageView } from './resizableImageView'
import * as styles from './styles'
import { AssetAttributes, AssetOptions, AssetStorage } from './types'
import { calculateImageDimensions } from './utils'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    asset: {
      /**
       * Adds the given asset by UUID and type at the current cursor position.
       */
      insertAsset: (asset: AssetAttributes) => ReturnType
    }
  }

  interface Storage {
    asset: AssetStorage
  }
}

export const Asset = Node.create<AssetOptions, AssetStorage>({
  name: 'asset',
  marks: '',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  // Prioritize above image extension
  priority: 99,

  addStorage() {
    return {
      assetStore: [],
      assetsLoading: true,
    }
  },

  addAttributes() {
    return {
      assetUuid: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-asset-uuid'),
        renderHTML: ({ assetUuid }: Pick<TextEditorAsset, 'assetUuid'>) =>
          assetUuid ? { 'data-asset-uuid': assetUuid } : undefined,
      },
      assetType: {
        default: 'image',
        parseHTML: (element) => element.getAttribute('data-asset-type'),
        renderHTML: ({ assetType }: Pick<TextEditorAsset, 'assetType'>) =>
          assetType ? { 'data-asset-type': assetType } : undefined,
      },
      assetName: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-asset-name') || '',
        renderHTML: ({ filename }: Pick<TextEditorAsset, 'filename'>) => ({
          'data-asset-name': filename || '',
        }),
      },
      assetSize: {
        default: 0,
        parseHTML: (element) => {
          const size = element.getAttribute('data-asset-size')
          return size ? parseInt(size, 10) : 0
        },
        renderHTML: ({ filesize }: Pick<TextEditorAsset, 'filesize'>) => ({
          'data-asset-size': filesize,
        }),
      },
      width: { default: null },
      height: { default: null },
    }
  },

  addKeyboardShortcuts() {
    return {
      // ProseMirror bug workaround so the last char between two nodes can be deleted.
      // See https://github.com/ueberdosis/tiptap/issues/3451
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          const { $from } = state.selection
          if (
            $from.nodeBefore?.isText &&
            $from.nodeBefore.text?.length === 1 &&
            $from.nodeAfter?.type.name === this.name
          ) {
            tr.delete($from.pos - 1, $from.pos)
            const newPos = tr.mapping.map($from.pos - 1)
            const prevNode = tr.doc.resolve(newPos).nodeBefore
            if (prevNode) {
              tr.setSelection(
                NodeSelection.create(tr.doc, newPos - prevNode.nodeSize)
              )
            }
            return true
          }
          return false
        }),
    }
  },

  addCommands() {
    return {
      insertAsset:
        ({ assetUuid, assetType }) =>
        ({ tr, dispatch, editor }) => {
          const { assetStore } = this.editor.storage.asset
          const asset = assetStore.find((a) => a.assetUuid === assetUuid)
          if (!asset) {
            console.warn(
              `Asset must exist in the assetStore prior to insert. If you
              called insertAsset immediately after uploading, you may
              need to wait for the next tick (use setTimeout with 0 delay)
              so the asset store update hook can run first.`
            )
            return false
          }

          const nodeType = assetType === 'image' ? 'asset' : 'resource'

          // Calculate dimensions for images based on aspect ratio
          const dimensions =
            assetType === 'image' && asset.width && asset.height
              ? calculateImageDimensions(asset.width, asset.height)
              : { width: undefined, height: undefined }

          const assetNode = createNodeFromContent(
            {
              type: nodeType,
              attrs: {
                assetUuid,
                assetType,
                assetName: asset.filename,
                assetSize: asset.filesize,
                width: dimensions.width,
                height: dimensions.height,
              },
            },
            editor.schema
          )

          if (dispatch) {
            const { parent } = tr.doc.resolve(tr.selection.from)
            const isEmptyTextBlock =
              parent.isTextblock && !parent.type.spec.code && !parent.childCount

            const pos =
              tr.selection.from === tr.selection.to &&
              this.type.isBlock &&
              isEmptyTextBlock
                ? tr.selection.from - 1
                : tr.selection.from

            tr.insert(pos, assetNode)

            // Select the newly inserted asset node to show resize handles on newly uploaded assets.
            tr.setSelection(NodeSelection.create(tr.doc, pos))
          }

          return true
        },
    }
  },

  addNodeView() {
    const reactNodeView = ReactNodeViewRenderer(AssetNodeView, {
      className: classNames(
        css(styles.nodeView(this.type.name)),
        'texteditor-asset-view'
      ),
    })

    return (props) => {
      const { node } = props
      const isImage = node.attrs.assetType === 'image'

      if (!isImage) {
        return reactNodeView(props)
      }

      const baseNodeView = reactNodeView(props)
      return resizableImageView(baseNodeView, props)
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        getAttrs: (node) => node.dataset.type === this.name && null,
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes)]
  },

  addProseMirrorPlugins() {
    const insertAsset = (asset: TextEditorAsset) =>
      this.editor.commands.insertAsset(asset)
    const insertHandler = createInsertHandler(
      this.options.onUploadAssets,
      insertAsset
    )

    return [
      new Plugin({
        key: new PluginKey('assetInsert'),
        props: {
          handleDrop: insertHandler<DragEvent>,
          handlePaste: insertHandler<ClipboardEvent>,
        },
      }),
    ]
  },
})

export type { AssetOptions, AssetStorage }
